import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import router from '@/router';

declare global {
    interface Window {
        BrazePlugin?: any;
    }
}

export async function initializePushNotifications(): Promise<void> {
    if (!Capacitor.isNativePlatform()) return;

    let permStatus = await PushNotifications.checkPermissions();

    if (permStatus.receive === 'prompt') {
        const result = await PushNotifications.requestPermissions();
        permStatus = result;
    }

    if (permStatus.receive !== 'granted') return;

    // Set opted_in immediately on confirmed permission. changeUser (called earlier on cold start)
    // resets push subscription to UNSUBSCRIBED for new Braze profiles — setting it here before
    // the token arrives ensures the correct state regardless of registration event timing.
    window.BrazePlugin?.setPushNotificationSubscriptionType('opted_in');

    // Register listener BEFORE register() so we never miss the registration event if the
    // FCM token is returned from cache before the JS listener is attached.
    PushNotifications.addListener('registration', ({ value: token }) => {
        window.BrazePlugin?.setRegisteredPushToken(token);
        window.BrazePlugin?.setPushNotificationSubscriptionType('opted_in');
    });

    // Register with FCM/APNs to get a device token.
    // Both BrazeFirebaseMessagingService and Capacitor's MessagingService are registered
    // for the same FCM intent — we bypass that conflict by getting the token here and
    // explicitly handing it to Braze rather than relying on service priority.
    await PushNotifications.register();

    PushNotifications.addListener('registrationError', (err) => {
        console.error('Push registration error:', err.error);
    });

    PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
        const data = action.notification.data;
        const url: string | undefined = data?.url ?? data?.deep_link;
        if (!url) return;

        try {
            const parsed = new URL(url);
            router.push(parsed.pathname + parsed.search + parsed.hash);
        } catch {
            router.push(url);
        }
    });
}
